/*
  cvc-recipe-transform.js

  A JavaScript mirror of scripts/transform.py.

  WHY THIS EXISTS
  ---------------
  Recipes are authored in Recipes Generator and normally reach this site through
  the Python pipeline (extract -> transform -> import -> index). That pipeline
  runs on a schedule, so there is a window where the site is behind the source.
  The live layer (cvc-recipe-live.js) closes that window by reading the vendor's
  storage straight from the visitor's browser.

  For that to be invisible, a recipe rendered live has to come out IDENTICAL to
  the same recipe rendered by the Python. If the two ever disagree - one stray
  em dash, one category spelled differently - then a freshly edited recipe looks
  subtly wrong next to its neighbours, which is worse than being briefly stale.

  So this file is a deliberate, literal port. Every function here has a named
  counterpart in transform.py and scripts/parity_check.js proves the two agree
  across all 708 real recipes. If you change transform.py, change this too and
  re-run the parity check - it is wired into scripts/sync.py and will fail the
  sync rather than let the two drift apart silently.

  PYTHON/JS TRAPS THAT ARE HANDLED HERE, because each one produced a real
  mismatch during development:
    - bool({}) is False in Python and true in JavaScript. videoSettings is often
      an empty object, so a naive !! showed videos Tammy had hidden. pyTruthy().
    - int("12.5") raises in Python (so the value becomes 0) while parseInt
      happily returns 12. pyInt().
    - "".strip() and "".trim() disagree on ﻿ and \x1c-\x1f. pyStrip().
    - Python slices strings by code point, JavaScript by UTF-16 code unit.
      Only matters above the BMP; slugify output is ASCII by then, so it does
      not bite, but take_chars() is used where it could.
*/
(function (root, factory) {
  var api = factory();
  if (typeof module === 'object' && module.exports) module.exports = api;   // node, for the parity check
  else root.CVCTransform = api;                                            // browser
})(typeof self !== 'undefined' ? self : this, function () {
  'use strict';

  // --- Python semantics ----------------------------------------------------

  // Python's bool(): empty dict, empty list, empty string, 0, None are all
  // false. JavaScript disagrees about {} and [], which is the whole reason
  // this helper exists.
  function pyTruthy(v) {
    if (v === null || v === undefined || v === false) return false;
    if (v === 0 || v === '') return false;
    if (Array.isArray(v)) return v.length > 0;
    if (typeof v === 'object') return Object.keys(v).length > 0;
    return !!v;
  }

  // Python's int(str) accepts optional sign and surrounding whitespace and
  // nothing else - "12.5", "12px" and "" all raise. Returns null on failure so
  // callers can apply their own fallback, exactly as the try/except does.
  function pyInt(v) {
    if (typeof v === 'number') return Number.isInteger(v) ? v : null;
    if (typeof v !== 'string') return null;
    return /^[+-]?\d+$/.test(v.trim()) ? parseInt(v.trim(), 10) : null;
  }

  // str.isdigit(): every character a digit, and not empty. ASCII is all this
  // data ever holds.
  function isDigits(s) { return /^\d+$/.test(s); }

  // Python's str.strip() with no argument removes ASCII whitespace plus the
  // Unicode space separators. It does NOT remove ﻿, which JS .trim() does.
  var PY_WS = '[ \\t\\n\\r\\f\\v\\x1c-\\x1f\\x85\\u00a0\\u1680\\u2000-\\u200a\\u2028\\u2029\\u202f\\u205f\\u3000]';
  var PY_STRIP_RE = new RegExp('^' + PY_WS + '+|' + PY_WS + '+$', 'g');
  function pyStrip(s) { return String(s === null || s === undefined ? '' : s).replace(PY_STRIP_RE, ''); }

  // Python's \s inside re, for str patterns.
  var WS_RUN = new RegExp(PY_WS + '+', 'g');

  // str[:n] counts code points, not UTF-16 units.
  function takeChars(s, n) { return Array.from(s).slice(0, n).join(''); }

  // str.strip(chars) - trim any of the given characters from both ends.
  function stripChars(s, chars) {
    var i = 0, j = s.length;
    while (i < j && chars.indexOf(s[i]) !== -1) i++;
    while (j > i && chars.indexOf(s[j - 1]) !== -1) j--;
    return s.slice(i, j);
  }

  // --- transform.py, function for function ---------------------------------

  var CATEGORY_FIXES = {
    'dessert': 'Desserts',
    'southern sides': 'Southern Sides',
    'bread & pastry': 'Bread & Pastry',
    'biscuits': 'Biscuits',
    'frosting filling & toppings': 'Frosting, Filling & Toppings',
    'sauce gravy & spices': 'Sauce, Gravy & Spices',
    'cornbread & dressing': 'Cornbread & Dressing'
  };
  var METHOD_FIXES = {
    'stovetop': 'Stovetop',
    'stove-top': 'Stovetop',
    'oven': 'Conventional Oven',
    'conventional oven': 'Conventional Oven',
    'slow cooker': 'Crockpot',
    'pressure cooker': 'Instant Pot',
    'baking': 'Baking'
  };

  var MASTER_RE = /\s*[-–]\s*Master Cookbook Recipe\s*#\s*(\d+)\s*$/i;
  // Mirrors NUMBER_RE in transform.py. Reads the cookbook number only; the
  // title and therefore the URL still come from MASTER_RE above.
  var NUMBER_RE = /\s*[-\u2013,]?\s*(?:Master\s+Cook(?:book)?(?:\s+Recip\w*)?|Recip\w*)\s*#\s*(\d+)\s*$/i;
  var TAG_RE = /<[^>]+>/g;

  function stripHtml(s) {
    if (!s) return '';
    s = String(s).replace(/<br\s*\/?>/gi, ' ');
    s = s.replace(/<\/(p|div|h\d|li)>/gi, ' ');
    return pyStrip(s.replace(TAG_RE, '').replace(WS_RUN, ' '));
  }

  var DASH_RE = /[—–]/;

  // See transform.py for the reasoning. Three cases, in this order:
  // a trailing signature becomes its own paragraph, a numeric range becomes
  // "10 to 12", and every other dash becomes a comma.
  function normaliseDashes(s) {
    if (!s || !DASH_RE.test(s)) return s;
    s = pyStrip(s).replace(
      /\s*[—–]\s*(Tammy|Chris)\s*(<\/p>)?\s*$/,
      function (_m, name, closing) {
        return closing ? ('</p><p>' + name + '</p>') : ('<br>' + name);
      }
    );
    s = s.replace(/(\d)\s*[—–]\s*(\d)/g, '$1 to $2');
    s = s.replace(/[—–]/g, ',');
    s = s.replace(/\s+,/g, ',');
    s = s.replace(/,\s*,/g, ',');
    return s;
  }

  function cleanInline(s) {
    if (!s) return '';
    s = String(s).replace(/\s*style="[^"]*"/g, '');
    s = s.replace(/\s*style='[^']*'/g, '');
    return pyStrip(s.replace(WS_RUN, ' '));
  }

  function slugify(s, maxlen) {
    maxlen = maxlen === undefined ? 80 : maxlen;
    var out = stripHtml(s).toLowerCase();
    out = out.split('&').join(' and ');
    out = stripChars(out.replace(/[^a-z0-9]+/g, '-'), '-');
    return stripChars(takeChars(out, maxlen), '-') || 'recipe';
  }

  function splitList(value) {
    if (!pyTruthy(value)) return [];
    var parts = Array.isArray(value) ? value.map(function (v) { return pyStr(v); })
                                     : pyStr(value).split(',');
    var out = [];
    for (var i = 0; i < parts.length; i++) {
      var p = parts[i];
      if (p && pyStrip(p)) out.push(pyStrip(p));
    }
    return out;
  }

  // Python's str() for the shapes this data actually contains.
  function pyStr(v) {
    if (v === null || v === undefined) return v === null ? 'None' : 'None';
    if (v === true) return 'True';
    if (v === false) return 'False';
    return String(v);
  }

  function normTerms(values, fixes) {
    var out = [], seen = {};
    for (var i = 0; i < values.length; i++) {
      var v = values[i];
      var key = pyStrip(v).toLowerCase();
      var canon = Object.prototype.hasOwnProperty.call(fixes, key) ? fixes[key] : pyStrip(v);
      canon = canon ? canon.charAt(0).toUpperCase() + canon.slice(1) : canon;
      if (!Object.prototype.hasOwnProperty.call(seen, canon.toLowerCase())) {
        seen[canon.toLowerCase()] = 1;
        out.push(canon);
      }
    }
    return out;
  }

  // ings/ins arrive as [{title, data:[html, ...]}, ...]
  function flattenGroups(groups) {
    var out = [];
    var list = groups || [];
    for (var i = 0; i < list.length; i++) {
      var g = list[i] || {};
      var items = [];
      var data = g.data || [];
      for (var j = 0; j < data.length; j++) {
        if (stripHtml(data[j])) items.push(normaliseDashes(cleanInline(data[j])));
      }
      if (items.length) out.push({ title: normaliseDashes(cleanInline(g.title || '')), items: items });
    }
    return out;
  }

  function youtubeId(url) {
    if (!url) return '';
    var m = String(url).match(/(?:youtu\.be\/|\/embed\/|\/shorts\/|\/live\/|v=)([A-Za-z0-9_-]{6,})/);
    if (m) return m[1];
    m = String(url).match(/youtube\.com\/([A-Za-z0-9_-]{6,})/);
    return m ? m[1] : '';
  }

  // datetime.fromtimestamp(ms/1000, utc).isoformat() - microseconds only when
  // non-zero, and a "+00:00" offset rather than JavaScript's "Z".
  function isoDate(ms) {
    var n = pyInt(ms);
    if (n === null) return '';
    var secs = Math.floor(n / 1000);
    var micros = (n - secs * 1000) * 1000;
    var d = new Date(secs * 1000);
    if (isNaN(d.getTime())) return '';
    var base = d.toISOString().slice(0, 19);
    var frac = micros ? '.' + String(micros).padStart(6, '0') : '';
    return base + frac + '+00:00';
  }

  function minutesFrom(t, hoursKey, minsKey) {
    var h = pyStrip(hoursKey in t ? pyStr(t[hoursKey]) : '');
    var m = pyStrip(minsKey in t ? pyStr(t[minsKey]) : '');
    return (isDigits(h) ? parseInt(h, 10) * 60 : 0) + (isDigits(m) ? parseInt(m, 10) : 0);
  }

  function transform(card, cardid) {
    card = card || {};
    var rawName = pyStrip(stripHtml(card.name || ''));
    var m = rawName.match(NUMBER_RE);
    var cookbookNo = m ? m[1] : '';
    var title = pyStrip(rawName.replace(MASTER_RE, '')) || rawName;

    var t = card.time || {};
    var prep = minutesFrom(t, 'ph', 'pm');
    var cook = minutesFrom(t, 'ch', 'cm');

    // Extra labelled durations are alternatives (Stovetop 1h OR Slow Cooker 6h),
    // not stages to add up, so they stay out of total_minutes.
    var customTime = [];
    var ct = t.customTime || [];
    for (var i = 0; i < ct.length; i++) {
      var c = ct[i];
      if (!c || typeof c !== 'object' || Array.isArray(c)) continue;
      var label = pyStrip(stripHtml(pyStr(c.label || '')));
      var hrs = pyTruthy(c.hours) ? c.hours : 0;
      var mns = pyTruthy(c.mins) ? c.mins : 0;
      var hi = pyInt(hrs), mi = pyInt(mns);
      var mins = (hi === null || mi === null) ? 0 : hi * 60 + mi;
      if (label || mins) customTime.push({ label: label, minutes: mins });
    }

    var products = [];
    var rawProducts = card.products || [];
    for (var p = 0; p < rawProducts.length; p++) {
      var pr = rawProducts[p] || {};
      if (!pyTruthy(pr.productLink)) continue;
      products.push({
        name: stripHtml(pr.productName || ''),
        link: pr.productLink || '',
        image: pr.productImage || ''
      });
    }

    var similar = [];
    var sps = card.similarPosts || [];
    for (var s = 0; s < sps.length; s++) {
      var sp = sps[s] || {};
      if (!pyTruthy(sp.cardId)) continue;
      var nm = stripHtml(sp.name || '');
      similar.push({
        name: nm,
        cardid: sp.cardId,
        handle: slugify(pyStrip(nm.replace(MASTER_RE, '')) || nm),
        image: sp.image || ''
      });
    }

    var nut = card.nutTablePerServing || card.nutInfo || {};
    // Zero is real information on a savoury dish, so only genuinely empty
    // values are dropped.
    var nutrition = {};
    Object.keys(nut).forEach(function (k) {
      if (pyStrip(pyStr(nut[k])) !== '') nutrition[k] = nut[k];
    });

    // The vendor only shows a video when videoSettings is set; five recipes
    // carry a videoUrl with videoSettings null and show nothing. Mirror that or
    // we surface videos Tammy chose to hide.
    var vid = card.videoUrl || '';
    var videoDisplay = pyTruthy(card.videoSettings);

    var categoriesRaw = splitList(card.categories);
    var cuisineRaw = splitList(card.cuisine).slice(0, 1);
    var methodsRaw = splitList(card.cookingMethod);

    return {
      cardid: pyStr(cardid),
      display_title: rawName,
      title: title,
      handle: slugify(title),
      author: stripHtml(card.author || 'Tammy Nichols'),
      summary_html: normaliseDashes(cleanInline(card.summary || '')),
      summary_text: stripHtml(normaliseDashes(card.summary || '')),
      notes_html: normaliseDashes(cleanInline(card.notes || '')),
      image: card.image || '',
      yields: normaliseDashes(stripHtml(card.yields || '')),
      // Mirrors transform.py: exact membership of the features array, and an
      // array is the only shape accepted, so the two cannot drift apart.
      scalable: (Array.isArray(card.features) ? card.features : []).indexOf('adjustable-servings') !== -1,
      prep_minutes: prep,
      cook_minutes: cook,
      total_minutes: prep + cook,
      custom_time: customTime,
      categories_raw: categoriesRaw,
      cuisine_raw: cuisineRaw,
      methods_raw: methodsRaw,
      categories: normTerms(categoriesRaw, CATEGORY_FIXES),
      cuisine: normTerms(cuisineRaw, {}).slice(0, 1),
      methods: normTerms(methodsRaw, METHOD_FIXES),
      cookbook_number: cookbookNo,
      nutrition: nutrition,
      nutrition_disclaimer: stripHtml(card.nutNotes || ''),
      youtube_url: vid,
      youtube_id: youtubeId(vid),
      video_display: videoDisplay,
      video_title: stripHtml(card.videoTitle || ''),
      video_desc: stripHtml(card.videoDescription || ''),
      video_thumb: card.videoThumbnail || '',
      video_date: card.videoUploadDate || '',
      ingredients: flattenGroups(card.ings),
      instructions: flattenGroups(card.ins),
      affiliate_products: products,
      similar: similar,
      date_created: isoDate(card.dateCreated),
      source_url: 'https://www.collardvalleycooks.com/recipes.html?cardid=' + cardid
    };
  }

  return {
    transform: transform,
    stripHtml: stripHtml,
    normaliseDashes: normaliseDashes,
    cleanInline: cleanInline,
    slugify: slugify,
    splitList: splitList,
    normTerms: normTerms,
    youtubeId: youtubeId,
    pyTruthy: pyTruthy,
    CATEGORY_FIXES: CATEGORY_FIXES,
    METHOD_FIXES: METHOD_FIXES
  };
});
